import React, { useEffect, useState } from 'react';
import {
  getGrupos, criarGrupo, editarGrupo, apagarGrupo, getArtigosVendus,
  detalhesErro, temMaisDe2CasasDecimais,
} from '../../../lib/faturacao';
import { getEstoqueProdutos } from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Sparkles, Plus, Pencil, Trash2, X, Link2, Link2Off, Loader2, AlertTriangle, Scale } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import { toast } from 'sonner';

const NOME_GRUPO_MAX = 80;
const NOME_OPCAO_MAX = 60;

const emptyOpcao = () => ({
  id: null, nome: '', preco: '0', ativa: true, vendus_ref: '',
  // O que a opção GASTA — texto vazio e não `null` porque os campos são
  // controlados, tal como o `vendus_ref` aqui ao lado.
  consumo: '', consumo_unidade: '', estoque_produto_id: '',
  // A unidade em que o ARTIGO conta. Vem do artigo escolhido e viaja com
  // ele: o movimento de stock não leva unidade nenhuma, e sem isto o
  // desconto adivinha — com um factor de mil pelo meio.
  estoque_unidade_medida: '',
});

// As mesmas do servidor (`catalogo.UNIDADES_DE_CONSUMO`). Escreve-se como se
// pensa — «30 g de granola» — e é o lado do Estoque que converte para a
// unidade em que conta.
const UNIDADES_DE_CONSUMO = ['g', 'kg', 'ml', 'L', 'un'];

// A marca do catálogo do Estoque a que esta faturação pertence. A Faturação
// é a das lojas L'Açaí; as outras marcas do Estoque (Lenha e Brasa, Purple
// House) não têm nada que ver com estes toppings.
const MARCA_DO_ESTOQUE = 'lacai';
const emptyForm = {
  nome: '', min_select: '0', max_select: '0', ativo: true, opcoes: [],
  // Só o servidor sabe se este grupo é o do TAMANHO (ver `openEdit`). Um
  // grupo novo ainda não é nada: a ligação aparece depois do primeiro
  // Guardar, que é quando o servidor responde sobre o nome escolhido.
  eVariante: false,
  // Mesmos defaults do servidor (catalogo.py): "opcoes" e sai_na_fatura=true —
  // um grupo novo abre já como os toppings de sempre, não como o Nome/Levar.
  tipo: 'opcoes', sai_na_fatura: true,
};

// Semântica DERIVADA (faturacao/catalogo.py): sem campos "obrigatorio" ou
// "tipo" no servidor — só min_select/max_select. Mostra-se por palavras aqui,
// nunca os números crus, mas os únicos campos enviados ao servidor continuam
// a ser min_select/max_select.
const regrasDoGrupo = (minSelect, maxSelect) => {
  const min = Number(minSelect) || 0;
  const max = Number(maxSelect) || 0;
  const obrigatorio = min >= 1;
  let escolha;
  if (max === 1) escolha = 'Escolha única';
  else if (max === 0) escolha = 'Sem limite';
  else escolha = `Até ${max} escolhas`;
  return { obrigatorio, escolha };
};

const fmtEUR = (n) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

export default function FatPersonalizacoes() {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [opcaoErrors, setOpcaoErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await getGrupos();
      setGrupos(data || []);
    } catch (error) {
      toast.error('Erro ao carregar personalizações');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError('');
    setOpcaoErrors({});
    setDialogOpen(true);
  };

  const openEdit = (grupo) => {
    setEditing(grupo);
    setForm({
      nome: grupo.nome || '',
      min_select: String(grupo.min_select ?? 0),
      max_select: String(grupo.max_select ?? 0),
      ativo: grupo.ativo !== false,
      tipo: grupo.tipo || 'opcoes',
      sai_na_fatura: grupo.sai_na_fatura !== false,
      eVariante: grupo.e_variante === true,
      opcoes: (grupo.opcoes || []).map((o) => ({
        id: o.id || null,
        nome: o.nome || '',
        preco: String(o.preco ?? 0),
        ativa: o.ativa !== false,
        // Vazio e não `undefined`: o input é controlado, e o Guardar
        // distingue "não ligado" de "ligado" pelo texto, não pela ausência.
        vendus_ref: o.vendus_ref || '',
        // `?? ''` e não `|| ''`: um consumo de 0 é uma resposta legítima (o
        // palito, o guardanapo) e `|| ''` apagava-o do ecrã ao abrir.
        consumo: o.consumo ?? '',
        consumo_unidade: o.consumo_unidade || '',
        estoque_produto_id: o.estoque_produto_id || '',
        estoque_unidade_medida: o.estoque_unidade_medida || '',
      })),
    });
    setFormError('');
    setOpcaoErrors({});
    setDialogOpen(true);
    // **O catálogo lê-se logo quando há ligações para mostrar.**
    //
    // Sem isto, um tamanho já ligado dizia «Ref. 145268982» — visto ao vivo,
    // com o CSS a sério. Mas a pergunta que o dono faz a este ecrã é «ligado
    // ao artigo CERTO?», e um número não a responde: só o nome do artigo o
    // faz.
    //
    // Só neste caso, e nunca no ecrã inteiro: um pedido ao Vendus de cada vez
    // que alguém abre o grupo dos Toppings era pagar uma chamada de rede para
    // não mostrar nada. Se falhar, não se diz nada aqui — as referências
    // continuam à vista e a escolha (que mostra o erro) continua a funcionar.
    if (grupo.e_variante && (grupo.opcoes || []).some((o) => o.vendus_ref)) {
      lerCatalogo();
    }
    // **E a mesma coisa para o Estoque, pela mesma razão exacta.**
    // `nomeDoProdutoDoEstoque` cai em «Artigo 6f2a-9c4e…» enquanto o catálogo
    // não estiver lido. O dono escrevia as gramagens de quinze toppings,
    // guardava, voltava no dia seguinte para conferir — e lia quinze uuids.
    //
    // Num grupo SEM ligações nenhumas não se pergunta nada: é de longe o caso
    // mais comum, e o Estoque é outro serviço, noutro servidor, que pode estar
    // em baixo. Só se pergunta quando há mesmo um nome para mostrar.
    if ((grupo.opcoes || []).some((o) => o.estoque_produto_id)) {
      lerCatalogoDoEstoque();
    }
  };

  // --- A ligação de cada TAMANHO ao artigo dele no Vendus --------------------
  //
  // No nosso catálogo há UM açaí e o tamanho é uma personalização dele; no
  // Vendus, o Mini, o Small, o Regular e o Supreme são quatro artigos. Sem
  // esta ligação, todas as linhas viajam com a referência do produto e as
  // lojas facturam tudo no mesmo artigo, seja qual for o tamanho.
  //
  // `eVariante` vem do SERVIDOR (`catalogo.listar_grupos`), que o calcula com
  // a mesma função que a emissão usa. Repetir aqui a regra de "isto é um
  // grupo de tamanho" era arranjar duas versões dela — e no dia em que
  // discordassem, o campo aparecia, era preenchido, e a emissão ignorava-o.
  const [artigosVendus, setArtigosVendus] = useState(null);
  const [artigosEstado, setArtigosEstado] = useState('inicio');
  const [artigosErro, setArtigosErro] = useState('');
  const [escolhaAberta, setEscolhaAberta] = useState(null); // índice da opção
  // A mesma máquina, para o catálogo do Estoque: índice da opção cujo artigo
  // se está a escolher, mais a lista lida a pedido (nunca ao abrir o ecrã —
  // o Estoque é outro serviço, noutro servidor, e pode estar em baixo).
  const [escolhaEstoque, setEscolhaEstoque] = useState(null);
  const [produtosEstoque, setProdutosEstoque] = useState([]);
  const [estoqueEstado, setEstoqueEstado] = useState('inicial');
  const [estoqueErro, setEstoqueErro] = useState('');
  const [filtroEstoque, setFiltroEstoque] = useState('');
  const [filtroArtigo, setFiltroArtigo] = useState('');

  // Lê o catálogo do Vendus uma vez, e só quando faz falta.
  const lerCatalogo = async () => {
    if (artigosVendus || artigosEstado === 'a-carregar') return;
    setArtigosEstado('a-carregar');
    setArtigosErro('');
    try {
      const { data } = await getArtigosVendus();
      setArtigosVendus(data || []);
      setArtigosEstado('pronto');
    } catch (error) {
      // A frase do servidor: é ele que sabe se a conta não está configurada
      // ou se o Vendus está em baixo. Uma lista vazia com ar de sucesso dizia
      // «esta conta não tem artigos» e o tamanho ficava por ligar por engano.
      setArtigosErro(detalhesErro(
        error, 'Não foi possível ler o catálogo do Vendus.').mensagem);
      setArtigosEstado('erro');
    }
  };

  const abrirEscolhaDeArtigo = (index) => {
    setEscolhaAberta(index);
    setFiltroArtigo('');
    lerCatalogo();
  };

  const escolherArtigo = (artigo) => {
    updateOpcao(escolhaAberta, { vendus_ref: artigo.id });
    setEscolhaAberta(null);
  };

  const lerCatalogoDoEstoque = async () => {
    setEstoqueEstado('a-carregar');
    setEstoqueErro('');
    try {
      const { data } = await getEstoqueProdutos(MARCA_DO_ESTOQUE);
      const produtos = data || [];
      setProdutosEstoque(produtos);
      setEstoqueEstado('pronto');
      // **Sara o que a Fase 1 deixou por saber.** As personalizações ligadas
      // antes deste campo existir têm artigo e não têm a unidade dele. Agora
      // que o catálogo está lido, sabe-se — e preencher aqui faz com que
      // guardar o grupo, por qualquer razão, deixe a ficha completa.
      //
      // Sem isto, uma ficha da Fase 1 continuava a não descontar nada (o
      // desconto salta o que não sabe converter) e o dono não tinha por onde
      // a compor: o ecrã não mostra a unidade do artigo em lado nenhum.
      setForm((prev) => ({
        ...prev,
        opcoes: prev.opcoes.map((o) => {
          if (!o.estoque_produto_id || o.estoque_unidade_medida) return o;
          const artigo = produtos.find((p) => String(p.id) === String(o.estoque_produto_id));
          return artigo ? { ...o, estoque_unidade_medida: artigo.unidade_medida || '' } : o;
        }),
      }));
    } catch (error) {
      // A frase do servidor, pela mesma razão que no Vendus: uma lista vazia
      // com ar de sucesso dizia «não há artigos no Estoque» e o topping ficava
      // por ligar por engano. O Estoque é outro serviço — 502 quando não
      // responde — e isso tem de se ler.
      setEstoqueErro(detalhesErro(
        error, 'Não foi possível ler o catálogo do Estoque.').mensagem);
      setEstoqueEstado('erro');
    }
  };

  const abrirEscolhaDoEstoque = (index) => {
    setEscolhaEstoque(index);
    setFiltroEstoque('');
    lerCatalogoDoEstoque();
  };

  const escolherProdutoDoEstoque = (produto) => {
    // Os dois campos ANDAM JUNTOS. Guardar só o id deixava o servidor a
    // recusar a gravação — e, pior, se ele aceitasse, o desconto ficava sem
    // saber se «30» são gramas ou quilos.
    updateOpcao(escolhaEstoque, {
      estoque_produto_id: produto.id,
      estoque_unidade_medida: produto.unidade_medida || '',
    });
    setEscolhaEstoque(null);
  };

  const nomeDoProdutoDoEstoque = (id) => {
    const produto = (produtosEstoque || []).find((p) => String(p.id) === String(id));
    // Sem o catálogo lido, o que se sabe é o id — e é o que se diz.
    return produto ? produto.nome : `Artigo ${id}`;
  };

  const produtosDoEstoqueFiltrados = React.useMemo(() => {
    const texto = filtroEstoque.trim().toLowerCase();
    if (!texto) return produtosEstoque || [];
    return (produtosEstoque || []).filter(
      (p) => `${p.nome} ${p.fornecedor || ''}`.toLowerCase().includes(texto));
  }, [produtosEstoque, filtroEstoque]);

  const nomeDoArtigo = (ref) => {
    const artigo = (artigosVendus || []).find((a) => String(a.id) === String(ref));
    // Sem o catálogo lido, o que se sabe é a referência — e é o que se diz.
    return artigo ? artigo.nome : `Ref. ${ref}`;
  };

  const artigosFiltrados = React.useMemo(() => {
    const texto = filtroArtigo.trim().toLowerCase();
    if (!texto) return artigosVendus || [];
    return (artigosVendus || []).filter(
      (a) => `${a.nome} ${a.referencia || ''}`.toLowerCase().includes(texto));
  }, [artigosVendus, filtroArtigo]);

  const addOpcao = () => setForm((prev) => ({ ...prev, opcoes: [...prev.opcoes, emptyOpcao()] }));
  const removeOpcao = (index) => {
    setForm((prev) => ({ ...prev, opcoes: prev.opcoes.filter((_, i) => i !== index) }));
    setOpcaoErrors((prev) => { const next = { ...prev }; delete next[index]; return next; });
  };
  const updateOpcao = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      opcoes: prev.opcoes.map((o, i) => (i === index ? { ...o, ...patch } : o)),
    }));
    setOpcaoErrors((prev) => ({ ...prev, [index]: undefined }));
  };

  const changeTipo = (tipo) => {
    setForm((prev) => ({
      ...prev,
      tipo,
      // "um grupo de texto não tem opções" (catalogo.py) — limpa-as ao mudar
      // para Texto livre, senão opções de uma escolha anterior seguiam
      // penduradas no payload sem aparecerem no ecrã (fantasmas no servidor).
      opcoes: tipo === 'texto' ? [] : prev.opcoes,
    }));
    setOpcaoErrors({});
    setFormError('');
  };

  const validar = () => {
    const nome = form.nome.trim();
    if (!nome) { toast.error('Indique o nome do grupo'); return false; }
    if (nome.length > NOME_GRUPO_MAX) { toast.error(`O nome não pode ter mais de ${NOME_GRUPO_MAX} caracteres`); return false; }

    const min = parseInt(form.min_select, 10) || 0;
    const max = parseInt(form.max_select, 10) || 0;
    if (min < 0 || max < 0) { setFormError('O mínimo e o máximo de escolhas não podem ser negativos'); return false; }
    // Mesma guarda condicional do model_validator do servidor (catalogo.py):
    // um grupo de texto não tem opções, e comparar min_select com
    // len(opcoes) recusava sempre um Nome marcado como obrigatório.
    if (form.tipo === 'opcoes') {
      if (max > 0 && min > max) {
        setFormError('O mínimo de escolhas não pode ser maior do que o máximo');
        return false;
      }
      if (min > form.opcoes.length) {
        setFormError('O mínimo de escolhas não pode ser maior do que o número de opções do grupo');
        return false;
      }
    }
    setFormError('');

    const erros = {};
    form.opcoes.forEach((opcao, i) => {
      const linhaErros = {};
      if (!opcao.nome.trim()) linhaErros.nome = 'Indique o nome da opção';
      else if (opcao.nome.trim().length > NOME_OPCAO_MAX) linhaErros.nome = `Máximo de ${NOME_OPCAO_MAX} caracteres`;
      const preco = Number(opcao.preco);
      if (opcao.preco === '' || !Number.isFinite(preco)) linhaErros.preco = 'Indique um preço válido';
      else if (preco < 0) linhaErros.preco = 'O preço não pode ser negativo';
      else if (temMaisDe2CasasDecimais(opcao.preco)) linhaErros.preco = 'Máximo de 2 casas decimais';
      // Espelho do `model_validator` do servidor (catalogo.py): ou número E
      // unidade, ou nenhum dos dois. Aqui só para o dono ver o erro no sítio
      // — quem manda é o servidor, e é lá que a regra vive a sério.
      const temConsumo = String(opcao.consumo ?? '').trim() !== '';
      const temUnidade = !!opcao.consumo_unidade;
      if (temConsumo !== temUnidade) {
        linhaErros.consumo = 'Indique a quantidade E a unidade (ex.: 30 g)';
      } else if (temConsumo) {
        const consumo = Number(opcao.consumo);
        if (!Number.isFinite(consumo)) linhaErros.consumo = 'Quantidade inválida';
        else if (consumo < 0) linhaErros.consumo = 'A quantidade não pode ser negativa';
      }
      if (Object.keys(linhaErros).length > 0) erros[i] = linhaErros;
    });
    setOpcaoErrors(erros);
    if (Object.keys(erros).length > 0) { toast.error('Há opções com dados inválidos'); return false; }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validar()) return;
    const payload = {
      nome: form.nome.trim(),
      min_select: parseInt(form.min_select, 10) || 0,
      max_select: parseInt(form.max_select, 10) || 0,
      ativo: form.ativo,
      tipo: form.tipo,
      sai_na_fatura: form.sai_na_fatura,
      opcoes: form.opcoes.map((o) => ({
        id: o.id || undefined,
        nome: o.nome.trim(),
        preco: Number(o.preco) || 0,
        ativa: o.ativa,
        // Sempre enviado, `null` quando não há ligação: omiti-lo deixava a
        // ligação anterior gravada e desligar um tamanho era impossível.
        vendus_ref: o.vendus_ref ? String(o.vendus_ref).trim() : null,
        // Os três do consumo, também sempre enviados e pela mesma razão. O
        // servidor preserva o que o pedido NÃO mencionar (é o que protege as
        // gramagens de um curl ou de um ecrã antigo) — mas este ecrã sabe do
        // assunto, e tem de conseguir apagar o que o dono apagou.
        consumo: String(o.consumo ?? '').trim() === '' ? null : Number(o.consumo),
        consumo_unidade: o.consumo_unidade || null,
        estoque_produto_id: o.estoque_produto_id || null,
        estoque_unidade_medida: o.estoque_unidade_medida || null,
      })),
    };
    setSaving(true);
    try {
      if (editing) { await editarGrupo(editing.id, payload); toast.success('Grupo de personalização atualizado'); }
      else { await criarGrupo(payload); toast.success('Grupo de personalização criado'); }
      setDialogOpen(false);
      fetchAll();
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        toast.error('Este grupo já não existe. A atualizar a lista...');
        setDialogOpen(false);
        fetchAll();
        return;
      }
      const { mensagem } = detalhesErro(error, 'Erro ao guardar o grupo de personalização');
      setFormError(mensagem);
      toast.error(mensagem);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const alvo = deleteTarget;
    setDeleteTarget(null);
    try {
      await apagarGrupo(alvo.id);
      toast.success('Grupo de personalização eliminado');
      fetchAll();
    } catch (error) {
      const status = error.response?.status;
      if (status === 409) {
        toast.error(error.response?.data?.detail || 'Este grupo ainda está atribuído a produtos. Retire-o dos produtos primeiro.');
      } else if (status === 404) {
        toast.error('Este grupo já não existe. A atualizar a lista...');
        fetchAll();
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao eliminar o grupo de personalização');
      }
    }
  };

  const previaRegras = regrasDoGrupo(form.min_select, form.max_select);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fat-personalizacoes-page">
      <PageHeader
        icon={Sparkles}
        title="Personalizações"
        subtitle="Grupos de toppings — depois atribuem-se aos produtos"
      >
        <Button onClick={openNew} data-testid="add-grupo-btn"><Plus className="h-4 w-4 mr-2" />Novo grupo</Button>
      </PageHeader>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div></div>
          ) : grupos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg">Sem grupos de personalização</h3>
              <p className="text-sm text-muted-foreground mt-1">Crie o primeiro grupo — por exemplo "Toppings" — e depois atribua-o aos produtos.</p>
            </div>
          ) : (
            <div className="divide-y">
              {grupos.map((grupo) => {
                const { obrigatorio, escolha } = regrasDoGrupo(grupo.min_select, grupo.max_select);
                const opcoes = grupo.opcoes || [];
                return (
                  <div key={grupo.id} className="p-4" data-testid={`grupo-row-${grupo.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{grupo.nome}</h3>
                          {grupo.ativo === false && (
                            <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200">Inativo</Badge>
                          )}
                          <Badge variant="outline" className={obrigatorio ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                            {obrigatorio ? 'Obrigatório' : 'Opcional'}
                          </Badge>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{escolha}</Badge>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {opcoes.length === 0 ? (
                            <span className="text-sm text-muted-foreground">Sem opções ainda.</span>
                          ) : (
                            opcoes.map((o) => (
                              <Badge key={o.id || o.nome} variant="secondary" className={o.ativa === false ? 'opacity-50' : ''}>
                                {o.nome} · {fmtEUR(o.preco)}
                              </Badge>
                            ))
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(grupo)} data-testid={`edit-grupo-${grupo.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(grupo)} data-testid={`delete-grupo-${grupo.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="grupo-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          {/* **UM diálogo de cada vez, e isto é a correcção de um defeito.**

              A escolha do artigo era um SEGUNDO `Dialog`, aberto por cima
              deste. Ao carregar num artigo, o de baixo fechava-se — o clique
              chegava-lhe como um toque «fora» — e o ecrã voltava à lista com
              a ligação por fazer. O dono viu-o ao vivo; os testes não, porque
              o arnês troca o `Dialog` por uma `div` e nunca chegou a montar
              dois.

              Aqui não há dois: o formulário dá lugar à lista de artigos e
              volta. O estado do formulário vive no componente, não no DOM,
              por isso trocar de vista não perde nada do que já foi escrito. */}
          {escolhaAberta !== null ? (
            <div data-testid="escolha-de-artigo">
              <DialogHeader>
                <DialogTitle>Ligar ao artigo do Vendus</DialogTitle>
                <DialogDescription>
                  Este tamanho passa a ser faturado no artigo escolhido. O preço e o
                  nome continuam a ser os nossos — a ligação decide só em nome de que
                  artigo a linha entra no Vendus.
                </DialogDescription>
              </DialogHeader>

              <div className="py-4 space-y-3">
                {artigosEstado === 'a-carregar' ? (
                  <div className="flex items-center justify-center h-32 gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">A ler o catálogo do Vendus…</span>
                  </div>
                ) : artigosEstado === 'erro' ? (
                  <div className="flex items-start gap-2 rounded-lg bg-destructive/10 text-destructive p-3 text-sm"
                    data-testid="erro-artigos-vendus">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{artigosErro}</span>
                  </div>
                ) : (
                  <>
                    <Input
                      value={filtroArtigo}
                      onChange={(e) => setFiltroArtigo(e.target.value)}
                      placeholder="Procurar por nome ou referência"
                      data-testid="filtro-artigo-do-tamanho"
                    />
                    <div className="max-h-72 overflow-y-auto divide-y rounded-md border">
                      {artigosFiltrados.length === 0 ? (
                        <p className="p-4 text-sm text-muted-foreground text-center">
                          Nenhum artigo com esse nome.
                        </p>
                      ) : artigosFiltrados.map((artigo) => (
                        <button
                          key={artigo.id}
                          type="button"
                          className="w-full text-left px-3 py-2.5 hover:bg-accent/60 flex items-center justify-between gap-3"
                          onClick={() => escolherArtigo(artigo)}
                          data-testid={`artigo-do-tamanho-${artigo.id}`}
                        >
                          <span className="text-sm truncate">{artigo.nome}</span>
                          <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                            {artigo.referencia || artigo.id}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline"
                  onClick={() => setEscolhaAberta(null)}
                  data-testid="voltar-do-artigo-btn">
                  Voltar sem ligar
                </Button>
              </DialogFooter>
            </div>
          ) : escolhaEstoque !== null ? (
            /* A mesma vista, para o catálogo do Estoque — e pela mesma razão
               de não ser um segundo diálogo: o formulário dá lugar à lista e
               volta com a escolha feita, sem perder o que já lá estava
               escrito. */
            <div data-testid="escolha-do-estoque">
              <DialogHeader>
                <DialogTitle>Escolher o artigo do Estoque</DialogTitle>
                <DialogDescription>
                  É deste artigo que a quantidade escrita vai sair do armazém a cada
                  venda. Nada desconta ainda — nesta fase só se escreve o que há-de
                  descontar.
                </DialogDescription>
              </DialogHeader>

              <div className="py-4 space-y-3">
                {estoqueEstado === 'a-carregar' ? (
                  <div className="flex items-center justify-center h-32 gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">A ler o catálogo do Estoque…</span>
                  </div>
                ) : estoqueEstado === 'erro' ? (
                  <div className="flex items-start gap-2 rounded-lg bg-destructive/10 text-destructive p-3 text-sm"
                    data-testid="erro-produtos-estoque">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{estoqueErro}</span>
                  </div>
                ) : (
                  <>
                    <Input
                      value={filtroEstoque}
                      onChange={(e) => setFiltroEstoque(e.target.value)}
                      placeholder="Procurar por nome ou fornecedor"
                      data-testid="filtro-artigo-do-estoque"
                    />
                    <div className="max-h-72 overflow-y-auto divide-y rounded-md border">
                      {produtosDoEstoqueFiltrados.length === 0 ? (
                        <p className="p-4 text-sm text-muted-foreground text-center">
                          Nenhum artigo com esse nome.
                        </p>
                      ) : produtosDoEstoqueFiltrados.map((produto) => (
                        <button
                          key={produto.id}
                          type="button"
                          className="w-full text-left px-3 py-2.5 hover:bg-accent/60 flex items-center justify-between gap-3"
                          onClick={() => escolherProdutoDoEstoque(produto)}
                          data-testid={`artigo-do-estoque-${produto.id}`}
                        >
                          <span className="text-sm truncate">{produto.nome}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {produto.unidade_medida || ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline"
                  onClick={() => setEscolhaEstoque(null)}
                  data-testid="voltar-do-estoque-btn">
                  Voltar sem escolher
                </Button>
              </DialogFooter>
            </div>
          ) : (
          <>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar grupo de personalização' : 'Novo grupo de personalização'}</DialogTitle>
            <DialogDescription>Ex.: "Toppings" com as opções Banana, Granola e Oreo grátis, e Nutella, Mel e Whey a pagar.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="grupo-nome">Nome *</Label>
                <Input
                  id="grupo-nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex: Toppings"
                  required
                  maxLength={NOME_GRUPO_MAX}
                  data-testid="grupo-nome-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Tipo *</Label>
                <RadioGroup
                  value={form.tipo}
                  onValueChange={changeTipo}
                  className="flex items-center gap-6"
                  data-testid="grupo-tipo-radio"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="opcoes" id="grupo-tipo-opcoes" />
                    <Label htmlFor="grupo-tipo-opcoes" className="cursor-pointer font-normal">Lista de opções</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="texto" id="grupo-tipo-texto" />
                    <Label htmlFor="grupo-tipo-texto" className="cursor-pointer font-normal">Texto livre</Label>
                  </div>
                </RadioGroup>
                <p className="text-xs text-muted-foreground">
                  Lista de opções — ao balcão, a funcionária escolhe entre alternativas já definidas (ex.: toppings).
                </p>
                <p className="text-xs text-muted-foreground">
                  Texto livre — ao balcão, a funcionária escreve uma resposta (ex.: o nome no copo).
                </p>
              </div>

              {form.tipo === 'opcoes' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="grupo-min">Mínimo de escolhas</Label>
                      <Input
                        id="grupo-min"
                        type="number"
                        min="0"
                        value={form.min_select}
                        onChange={(e) => { setForm({ ...form, min_select: e.target.value }); setFormError(''); }}
                        data-testid="grupo-min-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="grupo-max">Máximo de escolhas</Label>
                      <Input
                        id="grupo-max"
                        type="number"
                        min="0"
                        value={form.max_select}
                        onChange={(e) => { setForm({ ...form, max_select: e.target.value }); setFormError(''); }}
                        data-testid="grupo-max-input"
                      />
                      <p className="text-xs text-muted-foreground">0 = sem limite</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                    <span className="text-muted-foreground">Isto torna o grupo:</span>
                    <Badge variant="outline" className={previaRegras.obrigatorio ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                      {previaRegras.obrigatorio ? 'Obrigatório' : 'Opcional'}
                    </Badge>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{previaRegras.escolha}</Badge>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Switch
                    id="grupo-obrigatorio"
                    checked={Number(form.min_select) >= 1}
                    onCheckedChange={(v) => { setForm({ ...form, min_select: v ? '1' : '0' }); setFormError(''); }}
                    data-testid="grupo-obrigatorio-switch"
                  />
                  <Label htmlFor="grupo-obrigatorio" className="cursor-pointer">Resposta obrigatória</Label>
                </div>
              )}
              {formError && <p className="text-xs text-destructive" data-testid="grupo-form-error">{formError}</p>}

              <div className="flex items-center gap-2">
                <Switch id="grupo-ativo" checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} data-testid="grupo-ativo-switch" />
                <Label htmlFor="grupo-ativo" className="cursor-pointer">Grupo ativo</Label>
              </div>

              <div className="space-y-1.5 rounded-md border px-3 py-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="grupo-sai-na-fatura"
                    checked={form.sai_na_fatura}
                    onCheckedChange={(v) => setForm({ ...form, sai_na_fatura: v })}
                    data-testid="grupo-sai-na-fatura-switch"
                  />
                  <Label htmlFor="grupo-sai-na-fatura" className="cursor-pointer">Sai na fatura</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  As escolhas deste grupo aparecem na fatura do cliente. Desligue num grupo que seja só
                  para a cozinha — o nome no copo, ou levar/comer aqui.
                </p>
                <p className="text-xs text-muted-foreground">Uma opção paga aparece sempre, mesmo com isto desligado.</p>
              </div>

              {form.tipo === 'opcoes' && (
              <div className="space-y-2 pt-2 border-t">
                <div className="flex items-center justify-between pt-3">
                  <Label>Opções</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addOpcao} data-testid="add-opcao-btn">
                    <Plus className="h-3.5 w-3.5 mr-1.5" />Adicionar opção
                  </Button>
                </div>
                {form.opcoes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem opções. Adicione a primeira — ex.: "Banana", €0,00.</p>
                ) : (
                  <div className="space-y-2">
                    {form.opcoes.map((opcao, index) => (
                      <React.Fragment key={index}>
                      <div className="flex items-start gap-2" data-testid={`opcao-row-${index}`}>
                        <div className="flex-1 space-y-1">
                          <Input
                            value={opcao.nome}
                            onChange={(e) => updateOpcao(index, { nome: e.target.value })}
                            placeholder="Nome da opção"
                            maxLength={NOME_OPCAO_MAX}
                            aria-invalid={!!opcaoErrors[index]?.nome}
                            data-testid={`opcao-nome-input-${index}`}
                          />
                          {opcaoErrors[index]?.nome && <p className="text-xs text-destructive">{opcaoErrors[index].nome}</p>}
                        </div>
                        <div className="w-28 space-y-1">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={opcao.preco}
                            onChange={(e) => updateOpcao(index, { preco: e.target.value })}
                            placeholder="0.00"
                            aria-invalid={!!opcaoErrors[index]?.preco}
                            data-testid={`opcao-preco-input-${index}`}
                          />
                          {opcaoErrors[index]?.preco && <p className="text-xs text-destructive">{opcaoErrors[index].preco}</p>}
                        </div>
                        <div className="flex items-center h-10 gap-2 shrink-0">
                          <Switch
                            checked={opcao.ativa}
                            onCheckedChange={(v) => updateOpcao(index, { ativa: v })}
                            title="Opção ativa"
                            data-testid={`opcao-ativa-switch-${index}`}
                          />
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeOpcao(index)} data-testid={`remove-opcao-${index}`}>
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      {form.eVariante && (
                        <div className="pl-1 -mt-1" data-testid={`opcao-vendus-${index}`}>
                          {opcao.vendus_ref ? (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Link2 className="h-3.5 w-3.5 text-primary shrink-0" />
                              <span className="truncate">
                                Fatura como <span className="font-medium text-foreground">{nomeDoArtigo(opcao.vendus_ref)}</span>
                              </span>
                              <button
                                type="button" className="underline hover:text-foreground shrink-0"
                                onClick={() => abrirEscolhaDeArtigo(index)}
                                data-testid={`trocar-artigo-${index}`}
                              >Trocar</button>
                              <button
                                type="button" className="underline hover:text-destructive shrink-0"
                                onClick={() => updateOpcao(index, { vendus_ref: '' })}
                                data-testid={`desligar-artigo-${index}`}
                              >Desligar</button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground underline"
                              onClick={() => abrirEscolhaDeArtigo(index)}
                              data-testid={`ligar-artigo-${index}`}
                            >
                              <Link2Off className="h-3.5 w-3.5" />
                              Ligar ao artigo do Vendus
                            </button>
                          )}
                        </div>
                      )}
                      {/* **O que esta opção GASTA** — a linha que faz o stock
                          poder descer sozinho com as vendas.

                          Ao contrário da ligação ao Vendus aqui em cima, esta
                          aparece em TODOS os grupos e não só no do tamanho: a
                          granola gasta granola e o Regular gasta polpa, e as
                          duas coisas têm de se poder escrever. São espaços de
                          identificadores diferentes de propósito — o
                          `vendus_ref` decide em nome de que artigo se
                          FACTURA, este decide o que sai do ARMAZÉM. */}
                      <div
                        className="pl-1 -mt-1 flex flex-wrap items-center gap-2"
                        data-testid={`opcao-consumo-${index}`}
                      >
                        <Scale className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground">Gasta</span>
                        <Input
                          type="number"
                          step="0.001"
                          min="0"
                          value={opcao.consumo}
                          onChange={(e) => updateOpcao(index, { consumo: e.target.value })}
                          placeholder="—"
                          className="w-20 h-8 text-xs"
                          aria-invalid={!!opcaoErrors[index]?.consumo}
                          data-testid={`opcao-consumo-input-${index}`}
                        />
                        {/* `select` nativo e não o do desenho: são cinco
                            palavras numa lista fechada, o browser já sabe
                            desenhá-lo em qualquer tamanho de ecrã, e não
                            precisa de teclado próprio nem de foco emprestado. */}
                        <select
                          value={opcao.consumo_unidade}
                          onChange={(e) => updateOpcao(index, { consumo_unidade: e.target.value })}
                          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                          aria-label="Unidade do consumo"
                          data-testid={`opcao-consumo-unidade-${index}`}
                        >
                          <option value="">—</option>
                          {UNIDADES_DE_CONSUMO.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                        <span className="text-xs text-muted-foreground">de</span>
                        {opcao.estoque_produto_id ? (
                          <>
                            <span className="text-xs font-medium truncate">
                              {nomeDoProdutoDoEstoque(opcao.estoque_produto_id)}
                            </span>
                            <button
                              type="button" className="text-xs underline hover:text-foreground shrink-0"
                              onClick={() => abrirEscolhaDoEstoque(index)}
                              data-testid={`trocar-artigo-estoque-${index}`}
                            >Trocar</button>
                            <button
                              type="button" className="text-xs underline hover:text-destructive shrink-0"
                              onClick={() => updateOpcao(index, {
                estoque_produto_id: '', estoque_unidade_medida: '' })}
                              data-testid={`desligar-artigo-estoque-${index}`}
                            >Desligar</button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground underline"
                            onClick={() => abrirEscolhaDoEstoque(index)}
                            data-testid={`ligar-artigo-estoque-${index}`}
                          >
                            <Link2Off className="h-3.5 w-3.5" />
                            Escolher artigo do Estoque
                          </button>
                        )}
                        {opcaoErrors[index]?.consumo && (
                          <p className="w-full text-xs text-destructive">{opcaoErrors[index].consumo}</p>
                        )}
                      </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-grupo-btn">{saving ? 'A guardar...' : 'Guardar'}</Button>
            </DialogFooter>
          </form>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminação */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar grupo de personalização</AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que pretende eliminar "{deleteTarget?.nome}"? Esta ação não pode ser desfeita. Só é possível eliminar grupos que não estejam atribuídos a produtos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
